/**
 * End-to-End Encryption utilities for SENDBOX
 * Uses Web Crypto API (AES-256-GCM)
 * The encryption key never leaves the client - shared only via URL fragment
 */

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;

/**
 * Generate a new AES-256-GCM encryption key
 * @returns {Promise<CryptoKey>}
 */
export async function generateKey() {
  return await window.crypto.subtle.generateKey(
    { name: ALGORITHM, length: KEY_LENGTH },
    true,
    ['encrypt', 'decrypt']
  );
}

/**
 * Export key to base64url string (for URL fragment)
 * @param {CryptoKey} key
 * @returns {Promise<string>}
 */
export async function exportKey(key) {
  const rawKey = await window.crypto.subtle.exportKey('raw', key);
  return arrayBufferToBase64Url(rawKey);
}

/**
 * Import key from base64url string
 * @param {string} keyStr
 * @returns {Promise<CryptoKey>}
 */
export async function importKey(keyStr) {
  const rawKey = base64UrlToArrayBuffer(keyStr);
  return await window.crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ['decrypt']
  );
}

/**
 * Encrypt a file's ArrayBuffer
 * Format: [12 bytes IV][encrypted data with auth tag]
 * @param {ArrayBuffer} data
 * @param {CryptoKey} key
 * @returns {Promise<ArrayBuffer>}
 */
export async function encryptData(data, key) {
  const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const encrypted = await window.crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    key,
    data
  );
  // Prepend IV to encrypted data
  const result = new Uint8Array(iv.length + encrypted.byteLength);
  result.set(iv, 0);
  result.set(new Uint8Array(encrypted), iv.length);
  return result.buffer;
}

/**
 * Decrypt data that was encrypted with encryptData
 * @param {ArrayBuffer} encryptedData - [12 bytes IV][encrypted data]
 * @param {CryptoKey} key
 * @returns {Promise<ArrayBuffer>}
 */
export async function decryptData(encryptedData, key) {
  const dataView = new Uint8Array(encryptedData);
  const iv = dataView.slice(0, IV_LENGTH);
  const ciphertext = dataView.slice(IV_LENGTH);
  return await window.crypto.subtle.decrypt(
    { name: ALGORITHM, iv },
    key,
    ciphertext
  );
}

/**
 * Encrypt a File object, returns encrypted Blob
 * @param {File} file
 * @param {CryptoKey} key
 * @returns {Promise<Blob>}
 */
export async function encryptFile(file, key) {
  const data = await file.arrayBuffer();
  const encrypted = await encryptData(data, key);
  return new Blob([encrypted], { type: 'application/octet-stream' });
}

/**
 * Decrypt a Blob and return decrypted Blob
 * @param {Blob} encryptedBlob
 * @param {CryptoKey} key
 * @param {string} mimeType
 * @returns {Promise<Blob>}
 */
export async function decryptBlob(encryptedBlob, key, mimeType) {
  const data = await encryptedBlob.arrayBuffer();
  const decrypted = await decryptData(data, key);
  return new Blob([decrypted], { type: mimeType || 'application/octet-stream' });
}

// ============ URL-SAFE BASE64 HELPERS ============

function arrayBufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToArrayBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Check if Web Crypto API is available
 */
export function isEncryptionSupported() {
  return !!(window.crypto && window.crypto.subtle);
}
